<script setup lang="ts">
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { useQuasar } from 'quasar'
import { formatDateTime, formatNumber } from '../../utils/format'
import { useRestData } from '../../composables/useRestData'
import { useApi } from '../../composables/api'
import CrudDataTable from '../../components/CrudDataTable.vue'

const { adminUserFilesApi } = useApi()
const $q = useQuasar()
const bulkReprocessing = ref(false)

const bulkReprocess = (selected: any[], clearSelection: () => void) => {
  $q.dialog({
    title: 'Reprocess Files',
    message: `Requeue ${selected.length} file${selected.length !== 1 ? 's' : ''} for media processing?`,
    cancel: true,
    ok: { label: 'Reprocess', color: 'primary' }
  }).onOk(async () => {
    bulkReprocessing.value = true
    const results = await Promise.allSettled(
      selected.map((item) => adminUserFilesApi.adminUserFilesReprocessFileById({ id: item.id }))
    )
    const failed = results.filter((r) => r.status === 'rejected').length
    bulkReprocessing.value = false
    clearSelection()
    if (failed === 0) {
      $q.notify({
        type: 'positive',
        message: `${selected.length} file${selected.length !== 1 ? 's' : ''} queued for reprocessing.`
      })
    } else {
      $q.notify({
        type: 'warning',
        message: `${selected.length - failed} queued, ${failed} failed.`
      })
    }
  })
}
const route = useRoute()

// 1. Setup the headless data fetcher
const data = useRestData((params) => adminUserFilesApi.adminUserFilesPaginate(params), {
  initialPageSize: 25,
  defaultSort: [{ sortBy: 'createdAt', descending: true }]
})

if (route.query.id) {
  data.activeFilters.value['id'] = route.query.id as string
}

// Initial fetch
data.fetch()

// 2. Define the columns based on the UserFile type definition
const columns = [
  {
    name: 'uploadFileName',
    field: 'uploadFileName',
    label: 'File Name',
    align: 'left',
    sortable: true,
    format: (val: string) => val || ''
  },
  {
    name: 'uploadMimeType',
    field: 'uploadMimeType',
    label: 'MIME Type',
    align: 'left',
    sortable: true,
    format: (val: string) => val || ''
  },
  {
    name: 'uploadFileSize',
    field: 'uploadFileSize',
    label: 'Size (bytes)',
    align: 'right',
    sortable: true,
    format: (val: number) => (val ? formatNumber(val) : '')
  },
  {
    name: 'isUploaded',
    field: 'isUploaded',
    label: 'Uploaded',
    align: 'left',
    sortable: true,
    format: (val: boolean) => (val ? 'Yes' : 'No')
  },
  {
    name: 'createdAt',
    field: 'createdAt',
    label: 'Created',
    align: 'left',
    sortable: true,
    format: (val: string) => (val ? formatDateTime(val) : '')
  },
  {
    name: 'userProfileId',
    field: 'userProfileId',
    label: 'User Profile ID',
    align: 'left',
    sortable: true,
    format: (val: string) => val || ''
  },
  { name: 'actions', field: 'actions', label: 'Actions', align: 'right' }
]
</script>

<template>
  <q-page>
    <CrudDataTable
      title="Incident Report"
      :columns="columns"
      :rest-data="data"
      :update-api="(params) => adminUserFilesApi.adminUserFilesUpdateById(params)"
      :delete-api="(params) => adminUserFilesApi.adminUserFilesDeleteById(params)"
      enable-filters
      enable-multi-sort
      enable-search
      enable-selection
    >
      <template #bulk-actions="{ selected, clearSelection }">
        <q-btn
          flat
          dense
          icon="mdi-refresh"
          label="Reprocess"
          color="primary"
          :loading="bulkReprocessing"
          @click="bulkReprocess(selected, clearSelection)"
        />
      </template>

      <template #update-form="{ item }">
        <div class="row q-col-gutter-md">
          <div class="col-12 col-md-6">
            <q-input v-model="item.uploadFileName" label="File Name" outlined readonly />
          </div>
          <div class="col-12 col-md-6">
            <q-input v-model="item.uploadMimeType" label="MIME Type" outlined readonly />
          </div>
          <div class="col-12 col-md-6">
            <q-input
              v-model="item.uploadFileSize"
              label="File Size (bytes)"
              type="number"
              outlined
              readonly
            />
          </div>
          <div class="col-12 col-md-6">
            <q-toggle v-model="item.isUploaded" label="Uploaded" disable />
          </div>
          <div class="col-12 col-md-6">
            <q-input v-model="item.userProfileId" label="User Profile ID" outlined readonly />
          </div>
          <div class="col-12 col-md-6">
            <q-input v-model="item.createdAt" label="Created At" outlined readonly />
          </div>
        </div>
      </template>
    </CrudDataTable>
  </q-page>
</template>
